import {useCallback, useEffect, useMemo, useState} from 'react'
import {DropdownOptionProps} from 'wix-ui-tpa/cssVars'
import {useBi, useEnvironment, useTranslation} from '@wix/yoshi-flow-editor'
import {Status, TimeSlot} from '@wix/ambassador-table-reservations-v1-time-slot/types'
import {useSettings} from '@wix/tpa-settings/react'
import {ApprovalMode} from '@wix/ambassador-table-reservations-v1-reservation-location/types'
import {setHMToDate} from '@wix/table-reservations-lib/schedule'

import {useReservationsStorage} from '../storage'
import {filterDate, getNearestWorkingDate} from '../../../utils/date'
import {getBusinessScheduleFromReservationLocation} from '../../../utils/businessSchedule'
import {findTimeOption, getTimeOptions, TimeOption} from '../../../utils/timeOptions'
import {
  getInitialPartySizeFromOptions,
  getPartySizeOptionsForReservationLocation,
} from '../../../utils/partySize'
import {EDITOR_MOCK_RESERVATION_LOCATION_ID} from '../../../editor/editorMocks/reservationLocation'
import {FieldName, PageType, getLogger} from '../../../utils/getLogger'
import {
  getEnabledReservationLocations,
  getInitialReservationLocation,
  getReservationLocationById,
  getReservationLocationsOptions,
} from '../../../utils/reservationLocation'
import {
  ANY_AREA_ID,
  getAreaOptions,
  getVisibleAreas,
  getSelectedAreaPartySizeErrorKey,
  getSelectedAreaPartySizeRange,
  normalizeAreaId,
} from '../../../utils/areas'
import {scrollTo} from '../../../utils/scroll'
import {RequestStatus} from '../../../utils/wrapRequest'
import {reservationsFormDataHooks} from '../components/Form/constants'
import {useElementWidth} from '../../../utils/useElementWidth'
import {reserveButtonDataHooks} from '../components/ReserveButton/constants'
import {useGetLayoutSize} from '../../../components-shared/LayoutSizeProvider'
import {useApproveTextView} from '../../../utils/useApproveTextView'
import settingsParams from '../settingsParams'
import {getPreselectedDate} from '../../../utils/getPreselectedDate'
import {AppPage} from '../../../utils/constants'
import {readAndSetRwgToken} from '../../../utils/rwgToken'
import {getTimeSlotsPageType, TimeSlotsPageType} from '../../../utils/getTimeSlotsPageType'
import {getEffectiveFormLayout} from './formLayout'

import {SlotsSettingToStatus} from './constants'

export const useHooks = () => {
  const {t} = useTranslation()
  const bi = useBi()
  const logger = getLogger(bi)
  const {width: submitButtonContentWidth, readAndSetWidth: readAndSetSubmitButtonContentWidth} =
    useElementWidth<HTMLSpanElement>(reserveButtonDataHooks.button())

  const {layoutSize, containerWidth} = useGetLayoutSize()

  const {
    regionalSettings,
    getReservationLocationsStatus,
    getTimeSlotsStatus,
    reservationLocations,
    timeSlots,
    dayTimeSlots,
    selectedReservationLocationId: preselectedReservationLocationId,
    selectedAreaId: preselectedAreaId,
    selectedPartySize: preselectedPartySize,
    selectedDate: preselectedDate,
    getTimeSlots,
    getDayTimeSlots,
    getDayTimeSlotsStatus,
    prefetchCurrentMember,
    handleReservationData,
    handleReservationDataStatus,
    handleReservationDataError,
    metaSiteId,
    queryValidation,
    approvalTextEditorState,
    isSiteBusinessPremium,
    breadcrumbsConfig,
  } = useReservationsStorage()

  const isQueryValid =
    queryValidation.startDate && queryValidation.partySize && queryValidation.reservationLocationId
  const {isEditor, isPreview, isEditorX, isViewer} = useEnvironment()
  const settings = useSettings()
  const showSlotsBehavior = settings.get(settingsParams.showSlotsBehavior)

  const isNoReservationLocations = !reservationLocations.length

  const isTimeNotAvailableError =
    handleReservationDataError?.applicationError?.code === 'TIME_NOT_AVAILABLE'

  const enabledReservationLocations = getEnabledReservationLocations(reservationLocations)

  // reservations unavailable
  const [shouldShowReservationsUnavailable, setShouldShowReservationsUnavailable] = useState(false)

  const handleHideReservationsUnavailable = useCallback(() => {
    setShouldShowReservationsUnavailable(false)
  }, [])

  // reservation location
  const [selectedReservationLocation, setSelectedReservationLocation] = useState(
    getInitialReservationLocation(reservationLocations, preselectedReservationLocationId),
  )

  const reservationLocationsOptions: DropdownOptionProps[] = getReservationLocationsOptions(
    enabledReservationLocations,
    t,
  )

  const areaOptions = useMemo(
    () => getAreaOptions(selectedReservationLocation, t),
    [selectedReservationLocation, t],
  )
  const visibleAreas = useMemo(
    () => getVisibleAreas(selectedReservationLocation),
    [selectedReservationLocation],
  )
  const effectiveFormLayout = getEffectiveFormLayout({
    layoutSize,
    formLayout: settings.get(settingsParams.formLayout),
  })
  const [selectedAreaId, setSelectedAreaId] = useState(preselectedAreaId ?? ANY_AREA_ID)

  useEffect(() => {
    const selectedAreaUnavailable =
      selectedAreaId !== ANY_AREA_ID && !areaOptions.some(({id}) => id === selectedAreaId)

    if (selectedAreaUnavailable) {
      setSelectedAreaId(ANY_AREA_ID)
    }
  }, [areaOptions, selectedAreaId])

  // businessSchedule
  const businessSchedule = useMemo(
    () => getBusinessScheduleFromReservationLocation(selectedReservationLocation),
    [selectedReservationLocation],
  )

  // party size
  const partySizeOptions = useMemo(
    () => getPartySizeOptionsForReservationLocation(t, selectedReservationLocation),
    [selectedReservationLocation],
  )

  const [partySize, setPartySize] = useState(
    preselectedPartySize ?? getInitialPartySizeFromOptions(partySizeOptions),
  )
  const selectedAreaPartySizeRange = useMemo(
    () => getSelectedAreaPartySizeRange(selectedReservationLocation, selectedAreaId),
    [selectedReservationLocation, selectedAreaId],
  )
  const selectedAreaPartySizeErrorKey = useMemo(
    () => getSelectedAreaPartySizeErrorKey(selectedReservationLocation, selectedAreaId, partySize),
    [partySize, selectedAreaId, selectedReservationLocation],
  )

  // date
  const validPreselectedDate = getPreselectedDate({
    selectedReservationLocation,
    preselectedReservationLocationId,
    preselectedDate,
    queryValidation,
  })

  const [selectedDate, setSelectedDate] = useState<Date>(
    () =>
      validPreselectedDate ??
      getNearestWorkingDate({
        businessSchedule,
        regionalSettings,
        timeZone: selectedReservationLocation?.location?.timeZone,
      }),
  )

  // time
  const timeOptions = useMemo(
    () => getTimeOptions({timeSlots: dayTimeSlots, regionalSettings}),
    [dayTimeSlots, regionalSettings],
  )

  const [selectedTime, setSelectedTime] = useState<TimeOption | undefined>(() =>
    findTimeOption(timeOptions, selectedDate),
  )

  // manual approval
  const [isManualApproval, setIsManualApproval] = useState(false)

  useEffect(() => {
    const manualApprovalConfig =
      selectedReservationLocation?.configuration?.onlineReservations?.approval

    const partySizeThreshold =
      manualApprovalConfig?.manualForLargePartiesOptions?.partySizeThreshold || 0

    const shouldBeManualApprove =
      manualApprovalConfig?.mode === ApprovalMode.MANUAL ||
      (manualApprovalConfig?.mode === ApprovalMode.MANUAL_FOR_LARGE_PARTIES &&
        partySize >= partySizeThreshold)

    setIsManualApproval(shouldBeManualApprove)
  }, [partySize, selectedReservationLocation])

  // time slot
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<Date>()
  const [showSelectTimeSlotError, setShowSelectTimeSlotError] = useState(false)
  const [showNoAvailableTimeSlotNotification, setShowNoAvailableTimeSlotNotification] =
    useState(false)

  // BI
  const clickOnReservationsAttributesBiParams = useMemo(
    () => ({
      isPreview,
      isAddOn: false,
      isMultiLocation: !!enabledReservationLocations.length,
      locationId: selectedReservationLocation?.location?.id,
      reservationLocationId: selectedReservationLocation?.id,
    }),
    [enabledReservationLocations.length, isPreview, selectedReservationLocation],
  )

  const handleFilterDate = useCallback(
    (date: Date) =>
      filterDate({
        date,
        businessSchedule,
        timeZone: selectedReservationLocation?.location?.timeZone,
        regionalFormat: regionalSettings,
      }),
    [businessSchedule, selectedReservationLocation?.location?.timeZone, regionalSettings],
  )

  const handleLocationChange = (option: DropdownOptionProps) => {
    const newSelectedReservationLocation =
      getReservationLocationById(option.id!, enabledReservationLocations) ??
      enabledReservationLocations[0]

    setSelectedReservationLocation(newSelectedReservationLocation)
    setSelectedAreaId(ANY_AREA_ID)
    setSelectedTime(undefined)
    setSelectedTimeSlot(undefined)

    logger.clickOnReservationsAttributes({
      ...clickOnReservationsAttributesBiParams,
      value: option.id!,
      locationId: newSelectedReservationLocation?.location?.id,
      reservationLocationId: newSelectedReservationLocation?.id,
      fieldName: FieldName.location,
    })

    if (
      partySize <
        newSelectedReservationLocation?.configuration?.onlineReservations?.partySize?.min! ||
      partySize > newSelectedReservationLocation?.configuration?.onlineReservations?.partySize?.max!
    ) {
      setPartySize(
        newSelectedReservationLocation?.configuration?.onlineReservations?.partySize?.min!,
      )
    }
  }

  const handlePartySizeChange = (option: DropdownOptionProps) => {
    logger.clickOnReservationsAttributes({
      ...clickOnReservationsAttributesBiParams,
      value: option.id!,
      fieldName: FieldName.partySize,
    })

    setPartySize(Number(option.id))
    setSelectedTimeSlot(undefined)
    loadDayTimeSlots(selectedDate, Number(option.id))
  }

  const handleDateChange = (newDate: Date) => {
    logger.clickOnReservationsAttributes({
      ...clickOnReservationsAttributesBiParams,
      value: newDate.toISOString(),
      fieldName: FieldName.date,
    })

    setSelectedDate(newDate)
    setSelectedTimeSlot(undefined)

    loadDayTimeSlots(newDate)
  }

  const handleTimeChange = (newSelectedTime: TimeOption) => {
    logger.clickOnReservationsAttributes({
      ...clickOnReservationsAttributesBiParams,
      value: newSelectedTime.data.startDate.toISOString(),
      fieldName: FieldName.time,
    })

    setSelectedTime(newSelectedTime)
    setSelectedTimeSlot(undefined)

    loadTimeSlots(
      setHMToDate(selectedDate, newSelectedTime.data.hours, newSelectedTime.data.minutes),
    )
  }

  const handleAreaChange = (areaId: string) => {
    logger.clickOnReservationsAttributes({
      ...clickOnReservationsAttributesBiParams,
      value: areaId,
      fieldName: FieldName.area,
    })
    setSelectedAreaId(areaId)
    setSelectedTime(undefined)
    setSelectedTimeSlot(undefined)

    loadDayTimeSlots(selectedDate, partySize, areaId)
  }

  const handleTimeSlotChange = (item: TimeSlot) => {
    if (item.startDate) {
      logger.clickOnReservationsAttributes({
        ...clickOnReservationsAttributesBiParams,
        value: item.startDate.toISOString(),
        fieldName: FieldName.timeSlot,
      })
      setSelectedTimeSlot(item.startDate)
      setShowSelectTimeSlotError(false)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    readAndSetSubmitButtonContentWidth()

    if (isInitialDataInProgress || handleReservationDataStatus === RequestStatus.LOADING) {
      return
    }

    if (!selectedTimeSlot) {
      setShowSelectTimeSlotError(true)

      return
    }

    if (!selectedReservationLocation?.id) {
      return
    }

    if (!enabledReservationLocations.length || !isSiteBusinessPremium) {
      setShouldShowReservationsUnavailable(true)

      return
    }

    logger.clickOnReserveNow({
      locationId: selectedReservationLocation.location?.id!,
      reservationLocationId: selectedReservationLocation.id,
      isPreview,
      partySize,
      requestedDate: selectedTimeSlot,
    })

    const normalizedAreaId = normalizeAreaId(selectedAreaId)

    handleReservationData({
      details: {
        reservationLocationId: selectedReservationLocation.id,
        partySize,
        startDate: selectedTimeSlot,
        ...(normalizedAreaId ? {areaId: normalizedAreaId} : {}),
      },
      timeZone: selectedReservationLocation?.location?.timeZone!,
      isManualApproval,
    })
  }

  const handleSearchAgainClick = () => {
    if (selectedTime) {
      loadTimeSlots(setHMToDate(selectedDate, selectedTime.data.hours, selectedTime.data.minutes))
      scrollTo(reservationsFormDataHooks.root())
    }
  }

  const loadTimeSlots = (date?: Date, areaId = selectedAreaId) => {
    if (!selectedReservationLocation?.id || !date) {
      return
    }

    const normalizedAreaId = normalizeAreaId(areaId)

    getTimeSlots({
      reservationLocationId: selectedReservationLocation.id,
      date,
      partySize,
      timeSlotInterval:
        selectedReservationLocation.configuration?.onlineReservations?.timeSlotInterval,
      statuses: SlotsSettingToStatus[showSlotsBehavior],
      timeZone: selectedReservationLocation?.location?.timeZone,
      ...(normalizedAreaId ? {areaId: normalizedAreaId} : {}),
    })
  }

  const loadDayTimeSlots = (date?: Date, newPartySize = partySize, areaId = selectedAreaId) => {
    if (!selectedReservationLocation?.id || !date) {
      return
    }

    const normalizedAreaId = normalizeAreaId(areaId)

    getDayTimeSlots({
      reservationLocationId: selectedReservationLocation.id,
      date,
      partySize: newPartySize,
      timeSlotInterval:
        selectedReservationLocation.configuration?.onlineReservations?.timeSlotInterval,
      timeZone: selectedReservationLocation?.location?.timeZone,
      ...(normalizedAreaId ? {areaId: normalizedAreaId} : {}),
    })
  }

  useEffect(() => {
    const todaySlots = timeSlots[Object.keys(timeSlots)[0]]

    if (!todaySlots || !selectedTime) {
      return setShowNoAvailableTimeSlotNotification(false)
    }

    const selectedDateAndTime = setHMToDate(
      selectedDate,
      selectedTime.data.hours,
      selectedTime.data.minutes,
    )

    const targetSlot = todaySlots.find(
      (timeSlot) => timeSlot.startDate?.getTime() === selectedDateAndTime?.getTime(),
    )

    if (
      (!targetSlot || targetSlot.status !== Status.AVAILABLE) &&
      getTimeSlotsStatus === RequestStatus.RESOLVED
    ) {
      return setShowNoAvailableTimeSlotNotification(true)
    }

    setShowNoAvailableTimeSlotNotification(false)
  }, [timeSlots, selectedDate, selectedTime, getTimeSlotsStatus])

  const handleErrorClick = () => {
    window.location.href = window.location.href.split('?')[0]
  }

  const {shouldShowAutoApproveText} = useApproveTextView(
    enabledReservationLocations,
    approvalTextEditorState,
  )

  const isInitialDataInProgress =
    getReservationLocationsStatus !== RequestStatus.RESOLVED ||
    getDayTimeSlotsStatus !== RequestStatus.RESOLVED ||
    getTimeSlotsStatus !== RequestStatus.RESOLVED

  const isLargeParty =
    selectedReservationLocation?.configuration?.onlineReservations?.partySize?.max &&
    partySize > selectedReservationLocation.configuration.onlineReservations.partySize.max

  const phoneNumber = selectedReservationLocation?.location?.phone
    ? selectedReservationLocation.location.phone
    : undefined

  const shouldShowNoLocationsEditorWarning =
    (isEditor || isPreview) &&
    enabledReservationLocations[0].id === EDITOR_MOCK_RESERVATION_LOCATION_ID

  const reservationsPageType = useMemo(
    () => getTimeSlotsPageType(timeSlots, selectedDate),
    [timeSlots],
  )

  const shouldShowSearchAgainButton =
    reservationsPageType === TimeSlotsPageType.EXTENDED || isEditor

  useEffect(() => {
    logger.isLoaded({
      isAddOn: false,
      isPreview,
      pageType: PageType.page,
      pageName: AppPage.Reservations,
      areaSelectionDisplayed: areaOptions.length > 0,
      numAreas: visibleAreas.length,
      formLayout: effectiveFormLayout,
    })

    readAndSetRwgToken()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const newSelectedDate =
      validPreselectedDate ??
      getNearestWorkingDate({
        businessSchedule,
        regionalSettings,
        timeZone: selectedReservationLocation?.location?.timeZone,
      })

    setSelectedDate(newSelectedDate)
    loadDayTimeSlots(newSelectedDate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReservationLocation, businessSchedule, showSlotsBehavior])

  useEffect(() => {
    const preselectedTimeOption = validPreselectedDate
      ? findTimeOption(timeOptions, validPreselectedDate)
      : undefined

    const newSelectedTime =
      preselectedTimeOption ??
      timeOptions.find(
        ({id, data}) => id === selectedTime?.id && data.status === Status.AVAILABLE,
      ) ??
      timeOptions.find(({data}) => data.status === Status.AVAILABLE) ??
      timeOptions[0]

    if (newSelectedTime) {
      setSelectedTime(newSelectedTime)

      if (isViewer || isPreview) {
        loadTimeSlots(
          setHMToDate(selectedDate, newSelectedTime.data.hours, newSelectedTime.data.minutes),
        )
      }

      return
    }

    setSelectedTime(undefined)
    if (isViewer || isPreview) {
      loadTimeSlots(selectedDate)
    }
  }, [timeOptions])

  useEffect(() => {
    if (!isViewer && !isPreview) {
      loadTimeSlots(selectedDate)
    }
  }, [])

  useEffect(() => {
    if (handleReservationDataStatus === RequestStatus.FAILED) {
      setShouldShowReservationsUnavailable(true)
    }
  }, [handleReservationDataStatus, setShouldShowReservationsUnavailable])

  // should trigger time slots load for Editor X or Wix Studio
  // (Editor reinitialize controller but doesn't reinitialize the component)
  useEffect(() => {
    const shouldTriggerTimeSlotsLoad =
      isEditorX &&
      !Object.keys(timeSlots).length &&
      getTimeSlotsStatus === RequestStatus.DEFAULT &&
      getDayTimeSlotsStatus === RequestStatus.DEFAULT

    if (shouldTriggerTimeSlotsLoad) {
      loadTimeSlots(selectedDate)
      loadDayTimeSlots(selectedDate)
    }
  }, [
    isEditorX,
    timeSlots,
    getTimeSlotsStatus,
    getDayTimeSlotsStatus,
    loadTimeSlots,
    loadDayTimeSlots,
  ])

  useEffect(() => {
    prefetchCurrentMember()
  }, [prefetchCurrentMember])

  return {
    t,
    submitButtonContentWidth,
    isPreview,
    layoutSize,
    containerWidth,
    phoneNumber,
    regionalSettings,
    isLargeParty,
    isInitialDataInProgress,
    getTimeSlotsStatus,
    handleReservationDataStatus,
    isTimeNotAvailableError,
    reservationsPageType,
    partySizeOptions,
    timeOptions,
    partySize,
    selectedDate,
    selectedTime,
    selectedTimeSlot,
    timeSlots,
    areaOptions,
    selectedAreaId,
    selectedAreaPartySizeErrorKey,
    selectedAreaPartySizeRange,
    handleAreaChange,
    handlePartySizeChange,
    handleDateChange,
    handleTimeChange,
    handleTimeSlotChange,
    filterDate: handleFilterDate,
    handleSubmit,
    reservationLocationsOptions,
    selectedReservationLocation,
    handleLocationChange,
    shouldShowNoLocationsEditorWarning,
    metaSiteId,
    showSelectTimeSlotError,
    handleSearchAgainClick,
    isQueryValid,
    handleErrorClick,
    shouldShowSearchAgainButton,
    shouldShowReservationsUnavailable,
    handleHideReservationsUnavailable,
    isNoReservationLocations,
    shouldShowAutoApproveText,
    showNoAvailableTimeSlotNotification,
    breadcrumbsConfig,
    setShouldShowReservationsUnavailable,
  }
}
